import React, { Component } from 'react';
import { uniqueId, head } from 'lodash';
import classnames from 'classnames';
import { Button, Spinner } from 'ui';
import ErrorModal from 'components/Practice/shared/ErrorModal';
import trackEvent from 'misc/trackEvent';
import recordingPermissionErrorTypes from 'constants/recordingPermissionErrorTypes.js';
import { isSafari, isIE, isMobileDevice, isFirefox } from 'utils/browserCheck';
import InfoTooltip from 'components/shared/InfoTooltip';
import DropTarget from './dropTarget';

class HippoMediaRecorder extends Component {
  state = {
    loading: false,
    error: null,
    importTimeStarted: null,
    recording: false,
    recordingNotSupported: false,
  };

  componentDidMount() {
    if (isSafari() || isIE() || isMobileDevice()) {
      this.props.onBrowserRecordingNotSupported('all');
      this.setState({ recordingNotSupported: true });
    } else if (
      isFirefox() &&
      (this.props.isScreenRecording || this.props.multiStreamEnabled)
    ) {
      this.props.onBrowserRecordingNotSupported('screen');
      this.setState({ recordingNotSupported: true });
    }
  }

  recordingType = () => {
    if (
      (this.props.allowWebcam && this.props.isScreenRecording) ||
      this.props.multiStreamEnabled
    ) {
      return 'webcam screen';
    }
    if (this.props.allowScreen) {
      return 'screen';
    }
    if (this.props.allowWebcam) {
      return 'webcam';
    }
    return 'audio';
  };

  trackingEventParameters = () => ({
    lesson_section_element_id: this.props.elementId || null,
    progress_id: this.props.progressId || null,
    question_id: this.props.questionId || null,
    recording_type: this.recordingType(),
  });

  trackRecordingEvent = (event, data) => {
    if (!this.props.trackEvents) return;
    trackEvent(event, data);
  };

  handleCancel = () => {
    this.setState({ loading: false, recording: false });
  };

  handleVideoSubmitted = data => {
    const regex = /\/video\/play\/(.+)$/g;
    const mediaToken = regex.exec(data.recorded_url)[1];
    const embedUrl = data.recorded_url.replace('play', 'embed');
    const dataToSave = {
      url: `${data.recorded_url}?type=lessonly`,
      embedUrl,
      provider: 'hippo_video',
      token: mediaToken,
    };
    this.setState({ loading: false, recording: false });
    if (this.props.onUploading) {
      this.props.onUploading();
    }
    this.props.saveMedia(dataToSave);
  };

  trackSourceUploaded = () => {
    const eventData = {
      ...this.trackingEventParameters(),
      upload: true,
    };
    this.trackRecordingEvent('hippo_video/source_uploaded', eventData);
  };

  trackUploadStarted = () => {
    window.hippoWidget.on('import_progress', properties => {
      if (properties.operation === 'started') {
        const uploadStartedUrl =
          (this.props.events && this.props.events.upload_started) ||
          'recording_in_editor/upload_started';

        this.setState({ importTimeStarted: performance.now() });
        this.trackRecordingEvent(uploadStartedUrl, {
          element_id: this.props.elementId,
          question_id: this.props.questionId,
        });
      }
    });
  };

  trackUploadComplete = () => {
    window.hippoWidget.on('import_complete', data => {
      const uploadCompletedUrl =
        (this.props.events && this.props.events.upload_complete) ||
        'recording_in_editor/upload_complete';
      const importTimingFinal =
        performance.now() - this.state.importTimeStarted;
      this.trackRecordingEvent(uploadCompletedUrl, {
        element_id: this.props.elementId,
        question_id: this.props.questionId,
        token: data.token,
        duration: data.media_duration,
        total_import_time: importTimingFinal,
      });
      this.trackRecordingEvent('recording_metadata', {
        asset_id: data.asset_id,
        token: data.token,
      });
    });
  };

  uploadVideo = file => {
    window.hippoWidget.importFile(file, this.props.currentUserId);
    this.trackUploadStarted();
    window.hippoWidget.on('source_uploaded', this.trackSourceUploaded);
    this.trackUploadComplete();
    window.hippoWidget.on('video_submitted', this.handleVideoSubmitted);
    window.hippoWidget.on('video_cancelled', this.handleCancel);
  };

  handleUploadError = error => {
    if (error.reason === 'invalid_file_format') {
      const { canUseRecordAudioElement } = this.props;
      this.setState({
        error: canUseRecordAudioElement
          ? error.reason + '_audio'
          : error.reason + '_all',
      });
    } else if (error.reason === 'size_limit_exceeded') {
      this.setState({
        error: error.reason,
      });
    } else if (error.reason === 'processing_error') {
      if (error.error.includes('Media length is too short')) {
        this.setState({
          error: 'video_too_small',
        });
      } else if (
        error.error.includes('Video Codec Unidentified') ||
        error.error.includes('Audio Codec Unidentified')
      ) {
        this.setState({
          error: 'unidentified_codec',
        });
      }
    }
    this.setState({ loading: false });
  };

  handleFileChange = (e, droppedFiles) => {
    window.hippoWidget.on('import_failed', this.handleUploadError);
    const file = head(droppedFiles || e.target.files);

    if (!file) {
      return;
    }
    this.setState({ loading: true });
    this.uploadVideo(file);
  };

  handleRecordError = exception => {
    const errorType = exception.error;
    if (
      errorType !==
      recordingPermissionErrorTypes.HIPPO_SCREEN_PERMISSION_DENIED_ERROR
    ) {
      this.setState({ error: errorType });
    }
    this.setState({ loading: false });
  };

  trackRecordingStarted = () => {
    const eventData = {
      ...this.trackingEventParameters(),
    };

    this.setState({ recording: true });
    if (this.props.onRecording) {
      this.props.onRecording();
    }
    this.trackRecordingEvent('hippo_video/recording_started', eventData);
  };

  trackRecordingAborted = () => {
    const eventData = {
      ...this.trackingEventParameters(),
    };

    this.setState({ recording: false });
    if (this.props.onRecordingStopped) {
      this.props.onRecordingStopped();
    }

    this.trackRecordingEvent('hippo_video/recording_aborted', eventData);
  };

  trackRecordingStopped = () => {
    const eventData = {
      ...this.trackingEventParameters(),
    };

    this.setState({ recording: false });
    if (this.props.onRecordingStopped) {
      this.props.onRecordingStopped();
    }

    this.trackRecordingEvent('hippo_video/recording_stopped', eventData);
  };

  trackRecordingData = data => {
    this.trackRecordingEvent('recording_metadata', {
      asset_id: data.asset_id,
      token: data.token,
    });
  };

  trackRecordingInitiated = data => {
    const eventData = {
      ...this.trackingEventParameters(),
      token: data.token,
    };
    this.trackRecordingEvent('hippo_video/recording_initiated', eventData);
  };

  trackRecordingEvents = data => {
    this.trackRecordingInitiated(data);
    this.trackRecordingData(data);
  };

  handleRecord = () => {
    const recordingConfig = {
      screenRecord:
        this.props.isScreenRecording || this.props.multiStreamEnabled || false,
      audio: true,
      webCam: this.props.allowWebcam || this.props.multiStreamEnabled || false,
      resolution: '1080',
      tabRecording: false,
      separateLayer: false,
      fromPopup:
        !this.props.isScreenRecording || !this.props.multiStreamEnabled,
      initiator: 'generic_embed',
    };
    window.hippoWidget.on('record_error', this.handleRecordError);
    window.hippoWidget.startRecording(
      recordingConfig,
      this.props.currentUserId,
    );

    window.hippoWidget.on('record_initiated', this.trackRecordingEvents);
    window.hippoWidget.on('record_started', this.trackRecordingStarted);
    window.hippoWidget.on('record_aborted', this.trackRecordingAborted);
    window.hippoWidget.on('record_stopped', this.trackRecordingStopped);
    window.hippoWidget.on('video_submitted', this.handleVideoSubmitted);
    window.hippoWidget.on('video_cancelled', this.handleCancel);
  };

  render() {
    const { connectDropTarget } = this.props;

    const uploadId = uniqueId('upload');
    const fileInput = React.createRef();

    if (this.state.recording) {
      return <div>{t('lessons.editor.media_recording.recording')}</div>;
    }

    const recordButtonText =
      this.props.isScreenRecording ||
      this.props.multiStreamEnabled ||
      this.props.allowWebcam
        ? t('lessons.editor.media_recording.record')
        : t('questions.question.audio_recording.record');

    const uploadButtonText =
      this.props.isScreenRecording ||
      this.props.multiStreamEnabled ||
      this.props.allowWebcam
        ? t('lessons.editor.embeds.video.upload')
        : t('questions.question.audio_recording.upload');

    const renderButtons = () => {
      return (
        <div
          className={classnames({
            'w-fill': this.props.wide,
            'EditorElement-uploadArea EditorElement-uploadArea--dropZone ':
              this.props.allowDragAndDrop,
            'd-f jc-c': !this.props.allowDragAndDrop,
          })}
        >
          <div className="EditorElement-actionButtons d-f fd-r ai-c">
            <div className="d-f fd-c">
              {this.props.allowDragAndDrop && (
                <>
                  <div className="mt-24" />
                  <div className="ta-c fz-16">
                    {t('common.drag_and_drop')} {t('common.or')}
                  </div>
                  <div className="mt-12" />
                </>
              )}
              <Button
                className="mb-12"
                primary
                loading={this.props.loading}
                onClick={this.handleRecord}
                disabled={
                  this.props.disabled ||
                  this.props.loading ||
                  this.state.recordingNotSupported
                }
              >
                {recordButtonText}
              </Button>
              <div>
                {this.state.loading && <Spinner color="darker" />}
                {!this.state.loading && !this.state.recording && (
                  <div
                    className={classnames(
                      {
                        'NewButton--loading NewButton--disabled':
                          this.props.disabled || this.state.loading,
                      },
                      'EditorElement-uploadButton NewButton NewButton--center NewButton--white',
                    )}
                  >
                    <label className="fz-16" htmlFor={uploadId}>
                      {uploadButtonText}
                      <input
                        id={uploadId}
                        ref={fileInput}
                        type="file"
                        accept="video/quicktime, video/mp4, video/x-flv, video/*,audio/*"
                        onChange={this.handleFileChange}
                        disabled={this.props.disabled || this.state.loading}
                      />
                    </label>
                  </div>
                )}
              </div>
            </div>
            {this.state.recordingNotSupported && (
              <InfoTooltip
                tooltip={t('questions.question.media_recording.unsupported')}
                className="ml-16"
              />
            )}
            {this.state.error && (
              <ErrorModal
                errorType={this.state.error}
                onAction={() => {
                  this.setState({ error: null });
                }}
              />
            )}
          </div>
        </div>
      );
    };

    return (
      <>
        {this.props.allowDragAndDrop
          ? connectDropTarget(renderButtons())
          : renderButtons()}
      </>
    );
  }
}

export default props => {
  if (props.allowDragAndDrop) {
    const DropTargetHippoMediaRecorder = DropTarget(HippoMediaRecorder);
    return <DropTargetHippoMediaRecorder {...props} />;
  }
  return <HippoMediaRecorder {...props} />;
};
